import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Building2, Info, Copy, CalendarClock } from "lucide-react";
import MaintenanceLayout from "../shared/MaintenanceLayout";
import { getOrgSetup, cloneProfile, OrgSetupResponse, OrgSetupStation } from "../../../api/settlement";
import { useNotice } from "../shared/useNotice";

const card: React.CSSProperties = {
  background: "#fff",
  border: "1px solid #e0e0e0",
  borderRadius: 10,
  padding: 16,
  marginBottom: 16,
};

const primaryBtn: React.CSSProperties = {
  background: "#2563eb",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  padding: "8px 14px",
  cursor: "pointer",
  fontWeight: 600,
};

const ghostBtn: React.CSSProperties = {
  background: "#f1f3f5",
  color: "#333",
  border: "1px solid #e0e0e0",
  borderRadius: 8,
  padding: "6px 12px",
  cursor: "pointer",
};

const pillBase: React.CSSProperties = {
  display: "inline-block",
  padding: "2px 10px",
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 700,
};
const pillGreen: React.CSSProperties = { ...pillBase, background: "#e8f7ef", color: "#1a7f4b" };
const pillRed: React.CSSProperties = { ...pillBase, background: "#fdecec", color: "#c0392b" };

const th: React.CSSProperties = {
  textAlign: "left",
  padding: "8px 10px",
  fontSize: 12,
  fontWeight: 600,
  color: "#777",
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  borderBottom: "1px solid #e0e0e0",
};
const td: React.CSSProperties = {
  padding: "10px",
  fontSize: 14,
  color: "#333",
  borderBottom: "1px solid #f0f0f0",
};
const input: React.CSSProperties = { padding: "6px 8px", border: "1px solid #ccc", borderRadius: 6, fontSize: 13 };

const SettlementOrgSetup: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const orgId = Number(params.orgId);

  const [data, setData] = useState<OrgSetupResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useNotice("error", "");

  // inline "copy a profile into an unconfigured config"
  const [copyFor, setCopyFor] = useState<number | null>(null); // target pricing_override_id
  const [copySource, setCopySource] = useState<string>(""); // source profile_id
  const [copyBusy, setCopyBusy] = useState(false);
  const [copyError, setCopyError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await getOrgSetup(orgId));
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to load settlement setup for this org");
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSetup = (s: OrgSetupStation) => {
    // The rate card is built on the TAXABLE base (suggested_taxable). For an EXCLUSIVE
    // type the configured price IS the taxable (GST is added on top); for INCLUSIVE the
    // configured price is the gross and the taxable is de-grossed out of it. tax_model
    // tells the editor which story to show.
    const gstPct = s.gst_pct ?? 0;
    const taxModel = s.tax_model ?? "exclusive";
    const fallback = s.price_per_kwh;
    const taxable = s.configured
      ? s.taxable_unit_price ?? s.suggested_taxable ?? fallback
      : s.suggested_taxable ?? fallback;
    const base =
      `/maintenance/settlement/profile?pricing_override_id=${s.pricing_override_id}` +
      `&taxable=${taxable}&gst_pct=${gstPct}&tax_model=${taxModel}&org_id=${orgId}`;
    navigate(s.configured ? `${base}&profile_id=${s.profile_id}` : base);
  };

  const sources = (data?.stations || []).filter((s) => s.configured && s.profile_id != null);

  const handleCopy = async (targetPricingOverrideId: number) => {
    if (!copySource) {
      setCopyError("Pick a profile to copy from.");
      return;
    }
    setCopyBusy(true);
    setCopyError("");
    try {
      await cloneProfile(Number(copySource), targetPricingOverrideId);
      setCopyFor(null);
      setCopySource("");
      await load();
    } catch (err: any) {
      setCopyError(err?.response?.data?.detail || "Failed to copy profile.");
    } finally {
      setCopyBusy(false);
    }
  };

  return (
    <MaintenanceLayout title="Settlement · Set up">
      <button
        style={{ ...ghostBtn, display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 16 }}
        onClick={() => navigate("/maintenance/settlement")}
      >
        <ArrowLeft size={14} /> Back to settlements
      </button>

      {loading && <p style={{ color: "#777" }}>Loading settlement setup…</p>}

      {!loading && error && (
        <div style={{ ...card, borderColor: "#f5c6cb", background: "#fdecec", color: "#c0392b" }}>{error}</div>
      )}

      {!loading && !error && data && (
        <>
          {/* Header card */}
          <div style={card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: "#333", display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <Building2 size={18} color="#2563eb" /> {data.org_name}
                </h3>
                <div style={{ fontSize: 13, color: "#777", marginTop: 4 }}>GSTIN: {data.org_gstin || "—"}</div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
                <Link
                  to={`/maintenance/settlement/orgs/${orgId}/runs`}
                  style={{ ...primaryBtn, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}
                >
                  <CalendarClock size={15} /> View settlements
                </Link>
                <div style={{ fontSize: 13, color: "#777", textAlign: "right" }}>
                  {data.priced_configs} configs · {data.configured} configured · {data.missing} missing
                </div>
              </div>
            </div>
          </div>

          {/* Note */}
          <div style={{ ...card, display: "flex", gap: 10, alignItems: "flex-start", background: "#f8fafc" }}>
            <Info size={16} color="#2563eb" style={{ marginTop: 2, flexShrink: 0 }} />
            <p style={{ margin: 0, fontSize: 13, color: "#777" }}>
              Each price tier — a station priced for a specific consuming org — gets its own per-kWh split. Set up a
              settlement profile for every priced config so revenue earned there can be settled. You can copy an
              existing split into an unconfigured config and tweak it.
            </p>
          </div>

          {/* Stations table */}
          <div style={card}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={th}>Station</th>
                    <th style={th}>Type</th>
                    <th style={th}>Price/kWh</th>
                    <th style={th}>Source</th>
                    <th style={th}>Price tier</th>
                    <th style={th}>Status</th>
                    <th style={{ ...th, textAlign: "right" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.stations.map((s) => (
                    <tr key={s.pricing_override_id}>
                      <td style={td}>{s.station_name}</td>
                      <td style={td}>{s.charger_type}</td>
                      <td style={{ ...td, fontWeight: 600 }}>&#8377;{s.price_per_kwh}</td>
                      <td style={{ ...td, color: "#777" }}>{s.is_default ? "default" : `override · p${s.priority}`}</td>
                      <td style={{ ...td, color: "#777" }}>{s.price_org_name || "—"}</td>
                      <td style={td}>
                        {s.configured ? <span style={pillGreen}>Configured</span> : <span style={pillRed}>Needs profile</span>}
                      </td>
                      <td style={{ ...td, textAlign: "right" }}>
                        {s.configured ? (
                          <button style={ghostBtn} onClick={() => handleSetup(s)}>
                            Edit split
                          </button>
                        ) : (
                          <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                            <div style={{ display: "inline-flex", gap: 6 }}>
                              <button style={primaryBtn} onClick={() => handleSetup(s)}>
                                Set up split
                              </button>
                              {sources.length > 0 && (
                                <button
                                  style={{ ...ghostBtn, display: "inline-flex", alignItems: "center", gap: 4 }}
                                  onClick={() => {
                                    setCopyError("");
                                    setCopySource("");
                                    setCopyFor(copyFor === s.pricing_override_id ? null : s.pricing_override_id);
                                  }}
                                >
                                  <Copy size={13} /> Copy from…
                                </button>
                              )}
                            </div>
                            {copyFor === s.pricing_override_id && (
                              <div style={{ display: "inline-flex", flexWrap: "wrap", gap: 6, alignItems: "center", justifyContent: "flex-end" }}>
                                <select value={copySource} onChange={(e) => setCopySource(e.target.value)} style={{ ...input, minWidth: 200 }}>
                                  <option value="">Copy split from…</option>
                                  {sources.map((src) => (
                                    <option key={src.profile_id!} value={String(src.profile_id)}>
                                      {src.station_name} · {src.charger_type}
                                      {src.price_org_name ? ` · ${src.price_org_name}` : ""} (₹{src.taxable_unit_price})
                                    </option>
                                  ))}
                                </select>
                                <button
                                  style={{ ...primaryBtn, padding: "6px 10px", opacity: copyBusy ? 0.6 : 1 }}
                                  onClick={() => handleCopy(s.pricing_override_id)}
                                  disabled={copyBusy}
                                >
                                  {copyBusy ? "Copying…" : "Copy"}
                                </button>
                                <button style={ghostBtn} onClick={() => setCopyFor(null)}>
                                  Cancel
                                </button>
                                {copyError && <span style={{ color: "#c0392b", fontSize: 12 }}>{copyError}</span>}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {data.stations.length === 0 && (
                    <tr>
                      <td style={{ ...td, color: "#777" }} colSpan={7}>
                        No priced configs for this organisation yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </MaintenanceLayout>
  );
};

export default SettlementOrgSetup;
